import { useEffect, useState } from 'react';
import { useJugadoresPlantilla } from '@/features/plantillas-fantasy/GestionarPlantilla/hooks/useJugadoresPlantilla';
import { FANTASY_MODE } from '@/helpers/biwenger-helper';

/**
 * Calcula los rankings globales de jugadores para diferentes métricas
 * Los rankings se calculan de forma asíncrona sin bloquear el main thread
 *
 * @returns {Object} rankings - Objeto con rankings por playerId
 * @returns {Object} rankings[playerId] - Rankings de un jugador específico
 * @returns {number} rankings[playerId].laLigaFantasy - Ranking en La Liga Fantasy (TOP X)
 * @returns {number} rankings[playerId].biwenger - Ranking en Biwenger (TOP X)
 * @returns {number} rankings[playerId].fantasyMarca - Ranking en Fantasy Marca (TOP X)
 * @returns {number} rankings[playerId].misterFantasy - Ranking en Mister Fantasy (TOP X)
 * @returns {number} rankings[playerId].marketValueUp - Ranking de subidas de mercado (TOP X)
 * @returns {number} rankings[playerId].marketValue - Ranking de valor de mercado (TOP X)
 * @returns {number} rankings[playerId].marketValueDown - Ranking de bajadas de mercado (TOP X, negativo)
 */
export const useGlobalRankings = () => {
    const { jugadores } = useJugadoresPlantilla();
    const [rankings, setRankings] = useState({});
    const [isCalculating, setIsCalculating] = useState(false);

    // Calcular rankings de forma asíncrona sin bloquear
    useEffect(() => {
        if (!jugadores || jugadores.length === 0) {
            setRankings({});
            return;
        }

        setIsCalculating(true);

        // Usar requestIdleCallback si está disponible, sino setTimeout
        const scheduleCalculation = (callback) => {
            if (typeof window !== 'undefined' && window.requestIdleCallback) {
                window.requestIdleCallback(callback, { timeout: 2000 });
            } else {
                setTimeout(callback, 0);
            }
        };

        scheduleCalculation(() => {
            try {
                const calculatedRankings = calculateAllRankings(jugadores);
                setRankings(calculatedRankings);
            } catch (error) {
                console.error('Error calculando rankings globales:', error);
            } finally {
                setIsCalculating(false);
            }
        });
    }, [jugadores]);

    return { rankings, isCalculating };
};

/**
 * Calcula todos los rankings para todos los jugadores
 * @param {Array} jugadores - Lista de jugadores
 * @returns {Object} Rankings indexados por playerId
 */
function calculateAllRankings(jugadores) {
    const rankingsMap = {};

    // Calcular rankings por fantasy mode
    const laLigaFantasyRankings = calculateFantasyRankings(
        jugadores,
        (player) => player.laLigaFantasy?.modos?.find((m) => m.total?.points != null)?.total?.points ?? 0
    );

    const biwengerRankings = calculateFantasyRankings(
        jugadores,
        (player) =>
            player.biwenger?.modos?.find((m) => m.mode === FANTASY_MODE.AVERAGE && m.total?.points != null)
                ?.total?.points ?? 0
    );

    const fantasyMarcaRankings = calculateFantasyRankings(
        jugadores,
        (player) => player.fantasyMarca?.modos?.find((m) => m.total?.points != null)?.total?.points ?? 0
    );

    const misterFantasyRankings = calculateFantasyRankings(
        jugadores,
        (player) => player.misterFantasy?.modos?.find((m) => m.total?.points != null)?.total?.points ?? 0
    );

    // Calcular rankings de mercado
    const marketValueUpRankings = calculateMarketRankings(jugadores, (player) => {
        const subida = player.laLigaFantasy?.subida ?? 0;
        return subida > 0 ? subida : null; // Solo subidas positivas
    });

    const marketValueRankings = calculateMarketRankings(
        jugadores,
        (player) => player.laLigaFantasy?.marketValue ?? 0,
        true // Orden descendente (mayor valor primero)
    );

    const marketValueDownRankings = calculateMarketRankings(jugadores, (player) => {
        const subida = player.laLigaFantasy?.subida ?? 0;
        return subida < 0 ? Math.abs(subida) : null; // Solo bajadas (convertir a positivo para ranking)
    });

    // Combinar todos los rankings en un solo objeto indexado por playerId
    // Solo incluir jugadores que estén en top 15 de algún ranking
    jugadores.forEach((player) => {
        const playerId = player.playerId;
        const playerRankings = {};

        // Rankings de fantasy
        if (laLigaFantasyRankings[playerId] && laLigaFantasyRankings[playerId] <= 15) {
            playerRankings.laLigaFantasy = laLigaFantasyRankings[playerId];
        }
        if (biwengerRankings[playerId] && biwengerRankings[playerId] <= 15) {
            playerRankings.biwenger = biwengerRankings[playerId];
        }
        if (fantasyMarcaRankings[playerId] && fantasyMarcaRankings[playerId] <= 15) {
            playerRankings.fantasyMarca = fantasyMarcaRankings[playerId];
        }
        if (misterFantasyRankings[playerId] && misterFantasyRankings[playerId] <= 15) {
            playerRankings.misterFantasy = misterFantasyRankings[playerId];
        }

        // Rankings de mercado
        if (marketValueUpRankings[playerId] && marketValueUpRankings[playerId] <= 15) {
            playerRankings.marketValueUp = marketValueUpRankings[playerId];
        }
        if (marketValueRankings[playerId] && marketValueRankings[playerId] <= 15) {
            playerRankings.marketValue = marketValueRankings[playerId];
        }
        if (marketValueDownRankings[playerId] && marketValueDownRankings[playerId] <= 15) {
            playerRankings.marketValueDown = marketValueDownRankings[playerId];
        }

        // Solo agregar al mapa si tiene al menos un ranking en top 15
        if (Object.keys(playerRankings).length > 0) {
            rankingsMap[playerId] = playerRankings;
        }
    });

    return rankingsMap;
}

/**
 * Calcula rankings para un fantasy mode específico
 * @param {Array} jugadores - Lista de jugadores
 * @param {Function} getPoints - Función para obtener los puntos de un jugador
 * @returns {Object} Rankings indexados por playerId
 */
function calculateFantasyRankings(jugadores, getPoints) {
    // Crear array con playerId y puntos
    const playersWithPoints = jugadores
        .map((player) => ({
            playerId: player.playerId,
            points: getPoints(player),
        }))
        .filter((p) => p.points > 0); // Solo jugadores con puntos

    // Ordenar por puntos descendente
    playersWithPoints.sort((a, b) => b.points - a.points);

    // Crear mapa de rankings
    const rankingsMap = {};
    playersWithPoints.forEach((player, index) => {
        rankingsMap[player.playerId] = index + 1; // Ranking empieza en 1
    });

    return rankingsMap;
}

/**
 * Calcula rankings de mercado (subidas, valor, bajadas)
 * @param {Array} jugadores - Lista de jugadores
 * @param {Function} getValue - Función para obtener el valor de un jugador
 * @param {boolean} descending - Si true, ordena descendente (mayor primero)
 * @returns {Object} Rankings indexados por playerId
 */
function calculateMarketRankings(jugadores, getValue, descending = false) {
    // Crear array con playerId y valor
    const playersWithValues = jugadores
        .map((player) => ({
            playerId: player.playerId,
            value: getValue(player),
        }))
        .filter((p) => p.value != null && p.value !== 0); // Solo jugadores con valor válido

    // Ordenar por valor
    playersWithValues.sort((a, b) => {
        return descending ? b.value - a.value : a.value - b.value;
    });

    // Crear mapa de rankings
    const rankingsMap = {};
    playersWithValues.forEach((player, index) => {
        rankingsMap[player.playerId] = index + 1; // Ranking empieza en 1
    });

    return rankingsMap;
}
